import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { UUID } from '@/index'

interface SubmissionStateContextValue {
  readonly isSubmitting: (conversationId: UUID, sessionId: UUID) => boolean
  readonly setSubmitting: (conversationId: UUID, sessionId: UUID, isSubmitting: boolean) => void
}

const SubmissionStateContext = createContext<SubmissionStateContextValue | undefined>(undefined)

export const SubmissionStateProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [submittingStates, setSubmittingStates] = useState<Map<string, boolean>>(new Map())

  const makeKey = (conversationId: UUID, sessionId: UUID) => `${conversationId}-${sessionId}`

  const isSubmitting = useCallback(
    (conversationId: UUID, sessionId: UUID): boolean => {
      return submittingStates.get(makeKey(conversationId, sessionId)) ?? false
    },
    [submittingStates],
  )

  const setSubmitting = useCallback(
    (conversationId: UUID, sessionId: UUID, isSubmitting: boolean) => {
      setSubmittingStates((prev) => {
        const next = new Map(prev)
        const key = makeKey(conversationId, sessionId)
        if (isSubmitting) {
          next.set(key, true)
        } else {
          next.delete(key)
        }
        return next
      })
    },
    [],
  )

  const contextValue = useMemo(
    () => ({
      isSubmitting,
      setSubmitting,
    }),
    [isSubmitting, setSubmitting],
  )

  return (
    <SubmissionStateContext.Provider value={contextValue}>
      {children}
    </SubmissionStateContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSubmissionState() {
  const context = useContext(SubmissionStateContext)
  if (!context) throw new Error('useSubmissionState must be used within SubmissionStateProvider')
  return context
}
